import { CalendarClock, Info } from "lucide-react";

import { useCalendar } from "@/features/availability/model/useAvailability";
import { DateOverrideCalendar } from "@/features/availability/ui/DateOverrideCalendar";
import { MinRateEditor } from "@/features/availability/ui/MinRateEditor";
import { WeeklyTemplateEditor } from "@/features/availability/ui/WeeklyTemplateEditor";
import { useWorkerProfile } from "@/features/workers/model/useWorkerProfile";

export function AvailabilityPage() {
  const { data, isLoading, error } = useCalendar();
  const { data: profile } = useWorkerProfile();

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading your calendar…</div>;
  }
  if (error || !data) {
    return <div className="text-sm text-destructive">Could not load your calendar.</div>;
  }

  return (
    <div className="space-y-8">
      <header className="flex items-start gap-3">
        <div className="rounded-md bg-secondary p-2 text-secondary-foreground">
          <CalendarClock className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">My availability</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Set the hours you're free to work. Venues see all open shifts;{" "}
            <span className="font-medium">your shift feed</span> will filter to shifts that fit
            your calendar (toggle off to see everything).
          </p>
        </div>
      </header>

      {!data.initialized && (
        <div className="flex items-start gap-2 rounded-md border bg-card p-3 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            You haven't set any availability yet — every open shift is showing in your feed.
            Add a weekly schedule below to start filtering.
          </p>
        </div>
      )}

      {profile && (
        <section className="rounded-lg border bg-card p-6 shadow-sm">
          <div className="mb-4">
            <h3 className="text-base font-semibold">Minimum hourly rate</h3>
            <p className="text-xs text-muted-foreground">
              Shifts paying below this won't appear in your feed. Leave empty to
              see every shift you're qualified for.
            </p>
          </div>
          <MinRateEditor profile={profile} />
        </section>
      )}

      <section className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-baseline justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">Weekly schedule</h3>
            <p className="text-xs text-muted-foreground">
              The hours you're typically free, repeated every week. Add multiple windows per day
              for split shifts.
            </p>
          </div>
        </div>
        <WeeklyTemplateEditor template={data.template} />
      </section>

      <section className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-baseline justify-between gap-2">
          <div>
            <h3 className="text-base font-semibold">Date overrides</h3>
            <p className="text-xs text-muted-foreground">
              Block specific dates (vacation, school, anything) or set custom hours for a single
              day. Click any date to override.
            </p>
          </div>
        </div>
        <DateOverrideCalendar template={data.template} exceptions={data.exceptions} />
      </section>
    </div>
  );
}
